import React from 'react';
import i18n from '../../../i18n';
import { formatDrillMetric, METRICS } from '../data/metrics';
import type { DrillDownData, DrillPanel } from '../types';
import { Icon } from './shared/Icon';
import {
  DrillBlockedSection,
  DrillBrainSection,
  DrillCancelTimingSection,
  DrillDeadHoursSection,
  DrillDeck,
  DrillDefinitionsFooter,
  DrillGridSection,
  DrillGuestsHero,
  DrillOccupancyHeroStats,
  DrillHourProfileSection,
  DrillFunnelHeroStats,
  DrillFunnelSection,
  DrillGuardEffectSection,
  DrillImpactLine,
  DrillOpportunitySection,
  DrillOutletSection,
  DrillPosAttributionSection,
  DrillPosHoursSection,
  DrillPosItemsSection,
  DrillRankingSection,
  DrillRegimeSection,
  DrillRoomSection,
  DrillRepeatOffendersSection,
  DrillSellAttachSection,
  DrillSellNoShowSection,
  DrillSellShiftMixSection,
  DrillSourceSection,
  DrillTrendSection,
  DrillTurnsHeroStats,
  DrillTurnsShiftSection,
  DriversPanelSection,
  clearDrillFocus,
  driversSectionId,
  isCorrelatesPanel,
  isMultiDimPanel,
  isSoloLeadTimePanel,
  SellAttachHeroStats,
  type NarrativeAgentContext,
} from './drill/crux';
import {
  DrillMetricTabs,
  DrillPairLens,
  REVIEWS_TAB_KEY,
  drillPairFor,
  drillTabLedBy,
  type DrillTabDef,
} from './shared/DrillMetricTabs';
import { DrillToolbar, DrillToolbarStat } from './shared/DrillToolbar';
import { formatRangeLabel } from '../lib/perfSettings';

function TrendBadge({
  trendPct,
  trendUnit,
  trendLabel,
  goodHigh,
}: {
  trendPct: number | null;
  trendUnit?: string;
  trendLabel?: string;
  goodHigh: boolean;
}) {
  // No label: compare is off. No value: the compare window has no basis.
  if (trendPct == null || !trendLabel) return null;
  const trendGood = goodHigh ? trendPct >= 0 : trendPct <= 0;
  return (
    <div
      className="drill-hero-d drill-toolbar-trend"
      style={{ color: trendGood ? 'var(--st-steady)' : 'var(--st-alert)' }}
    >
      <Icon
        name={trendPct >= 0 ? 'arrowUp' : 'arrowDown'}
        size={13}
        stroke={2.4}
      />
      {Math.abs(trendPct)}
      {trendUnit ?? '%'} · {trendLabel}
    </div>
  );
}

export function PerformanceDrillDown({
  data,
  periodLabel,
  scopeLabel,
  onClose,
  onSwitchMetric,
  sourceBreakdown,
  connected,
  bucketLabels,
  aggregationLabel,
  comparable,
  agentCtx,
  onOpenReviews,
  reviewsAvailable,
  onSelectOutlet,
  scrollToSection,
  opportunityNarrative,
  opportunityNarrativeLoading,
  variant = 'overlay',
  focusScope,
  deckSuspended,
  showMetricTabs = true,
}: {
  data: DrillDownData;
  periodLabel: string;
  scopeLabel?: string;
  onClose: () => void;
  onSwitchMetric?: (tab: DrillTabDef) => void;
  sourceBreakdown?: import('../types').SourceBreakdown;
  connected?: import('../types').ConnectedSourceId[];
  bucketLabels?: string[];
  aggregationLabel?: string;
  comparable?: boolean;
  agentCtx?: NarrativeAgentContext;
  onOpenReviews?: () => void;
  reviewsAvailable?: boolean;
  onSelectOutlet?: (outletId: string) => void;
  scrollToSection?: 'blockedImpact';
  opportunityNarrative?: Extract<DrillPanel, { kind: 'narrative' }> | null;
  opportunityNarrativeLoading?: boolean;
  /**
   * 'drawer': the deck inside the outlet drawer, without the full-screen
   * shell and without a close button of its own (the drawer has one).
   */
  variant?: 'overlay' | 'drawer';
  /** Separate inspector memory for a deck on top of another (see DrillDeck). */
  focusScope?: string;
  /** The outlet drawer sits on top of this drill: leave Escape to it. */
  deckSuspended?: boolean;
  /** The drawer draws the metric tabs itself; the pair switch still shows. */
  showMetricTabs?: boolean;
}) {
  const m = METRICS[data.key];
  const m2 = data.secondary ? METRICS[data.secondary.key] : undefined;
  const moneyUnit =
    data.unit ||
    data.sourceBreakdown?.posUnit ||
    data.sourceBreakdown?.posCurrency ||
    null;
  const fmtVal = (v: number) => formatDrillMetric(v, data.key, moneyUnit);
  const fmtVal2 = data.secondary
    ? (v: number) => formatDrillMetric(v, data.secondary!.key, moneyUnit)
    : undefined;

  const groupVal = data.groupValue;
  const goodHigh = m?.better === 'high';
  const goodHigh2 = m2?.better === 'high';

  const bySourcePanel = data.panels?.find(
    (p): p is Extract<DrillPanel, { kind: 'bySource' }> =>
      p.kind === 'bySource',
  );
  const driversPanels = (data.panels ?? []).filter(
    (p): p is Extract<DrillPanel, { kind: 'drivers' }> =>
      p.kind === 'drivers',
  );
  const regimePanel = (data.panels ?? []).find(
    (p): p is Extract<DrillPanel, { kind: 'regimeGrid' }> =>
      p.kind === 'regimeGrid',
  );
  const blockedImpactPanel = (data.panels ?? []).find(
    (p): p is Extract<DrillPanel, { kind: 'blockedImpact' }> =>
      p.kind === 'blockedImpact',
  );
  const brainPanel = (data.panels ?? []).find(
    (p): p is Extract<DrillPanel, { kind: 'brainSeating' }> =>
      p.kind === 'brainSeating',
  );
  const posItemsPanel = (data.panels ?? []).find(
    (p): p is Extract<DrillPanel, { kind: 'posItems' }> =>
      p.kind === 'posItems',
  );
  const rankingPanels = (data.panels ?? []).filter(
    (p): p is Extract<DrillPanel, { kind: 'ranking' }> =>
      p.kind === 'ranking',
  );

  const mixBreakdown =
    data.sourceBreakdown?.sources?.length
      ? data.sourceBreakdown
      : sourceBreakdown;

  const handleSelectTab = (tab: DrillTabDef) => {
    if (tab.key === REVIEWS_TAB_KEY) {
      onOpenReviews?.();
      return;
    }
    onSwitchMetric?.(tab);
  };
  const showTabs =
    showMetricTabs && (!!onSwitchMetric || (reviewsAvailable && !!onOpenReviews));
  // A paired tab where both metrics are full drills (No-show & Cancellations)
  // carries a switch for which one leads the tiles.
  const pairSwitch = (() => {
    const pair = drillPairFor(data.key);
    return pair && pair.tab.switchable && pair.tab.secondaryKey ? pair : null;
  })();
  // The lens heads the body with both figures, so the header keeps only the lead.
  const showLens = !!(onSwitchMetric && pairSwitch && data.secondary && m2 && fmtVal2);
  const lensOption = (key: string) =>
    key === data.key
      ? {
          key,
          value: fmtVal(groupVal),
          trend: (
            <TrendBadge
              trendPct={data.trendPct}
              trendUnit={data.trendUnit}
              trendLabel={data.trendLabel}
              goodHigh={goodHigh ?? true}
            />
          ),
        }
      : {
          key,
          value: fmtVal2!(data.secondary!.groupValue),
          trend: (
            <TrendBadge
              trendPct={data.secondary!.trendPct}
              trendUnit={data.secondary!.trendUnit}
              trendLabel={data.trendLabel}
              goodHigh={goodHigh2 ?? true}
            />
          ),
        };

  // Weekdays picked on the grid narrow the hour profile; a new metric starts over.
  const [gridDows, setGridDows] = React.useState<number[]>([]);
  React.useEffect(() => setGridDows([]), [data.key]);
  const toggleGridDow = (dow: number) =>
    setGridDows(cur => (cur.includes(dow) ? cur.filter(d => d !== dow) : [...cur, dow]));

  // A remembered inspector tile only reopens for the same scope, outlet and period.
  const focusContext = [
    periodLabel,
    scopeLabel ?? '',
    agentCtx?.outletId ?? '',
    (data.outlets ?? []).map(o => o.id).join(','),
  ].join('|');
  const handleClose = () => {
    clearDrillFocus(focusScope);
    onClose();
  };
  const inDrawer = variant === 'drawer';

  React.useEffect(() => {
    if (scrollToSection !== 'blockedImpact' || !blockedImpactPanel) return;
    const timer = window.setTimeout(() => {
      document
        .getElementById('drill-section-blocked-impact')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [scrollToSection, blockedImpactPanel, data.key]);

  return (
    <div
      className={inDrawer ? 'drill-embedded' : 'drill drill-below-topbar'}
      data-ftue={inDrawer ? undefined : 'perf-drill'}
    >
      <DrillToolbar
        onClose={inDrawer ? undefined : handleClose}
        periodLabel={periodLabel}
        hero={
          <>
            <DrillToolbarStat
              label={m?.short ?? data.key}
              value={fmtVal(groupVal)}
              trend={
                <TrendBadge
                  trendPct={data.trendPct}
                  trendUnit={data.trendUnit}
                  trendLabel={data.trendLabel}
                  goodHigh={goodHigh ?? true}
                />
              }
            />
            <DrillGuestsHero
              hero={data.guestsHero}
              compareLabel={data.comparable ? data.comparePeriod?.label : undefined}
            />
            {data.funnel ? (
              <DrillFunnelHeroStats
                funnel={data.funnel}
                trendLabel={data.trendLabel}
                Trend={TrendBadge}
              />
            ) : null}
            {data.secondary && m2 && !data.funnel && !showLens && (
              <DrillToolbarStat
                label={m2.short}
                value={fmtVal2!(data.secondary.groupValue)}
                secondary
                trend={
                  <TrendBadge
                    trendPct={data.secondary.trendPct}
                    trendUnit={data.secondary.trendUnit}
                    trendLabel={data.trendLabel}
                    goodHigh={goodHigh2 ?? true}
                  />
                }
              />
            )}
            {!data.secondary && (
              <DrillTurnsHeroStats
                capacity={data.turnsCapacity}
                compareLabel={data.comparable ? data.comparePeriod?.label : undefined}
              />
            )}
            {data.key === 'occupancy' && (
              <DrillOccupancyHeroStats occupancy={data.seatHourOccupancy} />
            )}
            {data.sellAttach && (
              <SellAttachHeroStats
                data={data.sellAttach}
                fmtMoney={fmtVal}
                trendLabel={data.comparable ? data.trendLabel : undefined}
              />
            )}
          </>
        }
      >
        {showTabs && (
          <DrillMetricTabs
            active={data.key}
            reviewsAvailable={reviewsAvailable}
            connected={connected}
            onSelect={handleSelectTab}
          />
        )}
      </DrillToolbar>

      <div className="drill-body">
        {showLens && pairSwitch ? (
          <DrillPairLens
            active={data.key}
            options={[
              lensOption(pairSwitch.tab.key),
              lensOption(pairSwitch.tab.secondaryKey!),
            ]}
            onSelect={key => onSwitchMetric!(drillTabLedBy(pairSwitch.tab, key))}
          />
        ) : null}
        <DrillImpactLine impact={data.impact} />
        <DrillDeck
          metricKey={data.key}
          contextKey={focusContext}
          focusScope={focusScope}
          suspended={deckSuspended}
        >
          <DrillOpportunitySection
            metricKey={data.key}
            narrative={opportunityNarrative}
            loading={opportunityNarrativeLoading}
            agentCtx={agentCtx}
          />

          <DrillTrendSection
            metricKey={data.key}
            secondaryKey={data.secondary?.key}
            series={data.series}
            secondarySeries={data.secondary?.series}
            compareSeries={data.comparable ? data.compareSeries : undefined}
            compareBucketLabels={
              data.comparable
                ? data.compareBuckets?.map(b => formatRangeLabel(b.start, b.end))
                : undefined
            }
            seriesLabel={
              data.seriesLabel ??
              i18n.t('performance:drillUi.overTime', { label: m?.short ?? data.key })
            }
            secondarySeriesLabel={data.secondarySeriesLabel}
            compareLabel={data.comparable ? data.comparePeriod?.label : undefined}
            bucketLabels={bucketLabels}
            periodValue={data.groupValue}
            periodLabel={periodLabel}
            trendPct={data.trendPct}
            trendUnit={data.trendUnit}
            trendLabel={data.trendLabel}
            secondaryValue={data.secondary?.groupValue}
            secondaryTrendPct={data.secondary?.trendPct}
            secondaryTrendUnit={data.secondary?.trendUnit}
            secondaryGoodHigh={goodHigh2}
            fmtVal={fmtVal}
            fmtVal2={fmtVal2}
            basis={data.basis}
          />

          <DrillSourceSection
            metricKey={data.key}
            secondaryKey={data.secondary?.key}
            breakdown={mixBreakdown}
            bySourcePanel={bySourcePanel}
            connected={connected}
            bucketLabels={bucketLabels}
            aggregationLabel={aggregationLabel}
            periodLabel={periodLabel}
            comparable={data.comparable ?? comparable}
            compareLabel={data.comparable ? data.comparePeriod?.label : undefined}
            moneyUnit={moneyUnit}
            metricValue={data.groupValue}
            metricSeries={data.series}
            fmtVal={fmtVal}
            fmtVal2={fmtVal2}
          />

          {data.grid ? (
            <DrillGridSection
              grid={data.grid}
              fmtVal={fmtVal}
              compareLabel={data.comparable ? data.comparePeriod?.label : undefined}
              span={data.hourProfile ? 8 : 12}
              selectedDows={gridDows}
              onToggleDow={data.hourProfile ? toggleGridDow : undefined}
            />
          ) : null}
          {data.hourProfile ? (
            <DrillHourProfileSection
              profile={data.hourProfile}
              selectedDows={gridDows}
              dayLabels={new Map(data.grid?.rows.map(r => [r.dow, r.label]))}
              onClear={() => setGridDows([])}
              span={data.grid ? 4 : 12}
            />
          ) : null}
          {data.funnel ? (
            <DrillFunnelSection
              funnel={data.funnel}
              compareLabel={data.comparable ? data.comparePeriod?.label : undefined}
            />
          ) : null}
          <DrillCancelTimingSection timing={data.cancelTiming} />
          <DrillGuardEffectSection effects={data.guardEffects} />
          <DrillRepeatOffendersSection repeat={data.repeatNoShows} />
          {data.posHours && (
            <DrillPosHoursSection metricKey={data.key} data={data.posHours} unit={moneyUnit} />
          )}
          {data.posAttribution && (
            <DrillPosAttributionSection
              metricKey={data.key}
              data={data.posAttribution}
              unit={moneyUnit}
            />
          )}
          {data.sellAttach && (
            <DrillSellAttachSection
              metricKey={data.key}
              data={data.sellAttach}
              fmtMoney={fmtVal}
              compareLabel={data.comparable ? data.comparePeriod?.label : undefined}
            />
          )}
          {data.sellShiftMix && (
            <DrillSellShiftMixSection metricKey={data.key} data={data.sellShiftMix} fmtMoney={fmtVal} />
          )}

          {(() => {
            const leadPanel = driversPanels.find(isSoloLeadTimePanel);
            // The drill metric's own multi-dimension panel is the correlates
            // tile; any other multi-dimension panel (seat time on Turns) is a
            // lever tile of its own below the correlates | outlets row.
            const correlates = driversPanels.find(dp =>
              isCorrelatesPanel(dp, data.key),
            );
            const levers = driversPanels.filter(
              dp => isMultiDimPanel(dp) && dp !== correlates,
            );
            const foldLead =
              correlates && leadPanel
                ? leadPanel.dimensions?.[0] ?? null
                : null;
            // Single-dimension panels other than lead time keep their slot
            // above the row.
            const singles = driversPanels.filter(
              dp => !isMultiDimPanel(dp) && dp !== leadPanel,
            );
            const showOutlets = (data.outlets?.length ?? 0) > 1;
            const driversSpan = showOutlets ? 6 : 12;
            const renderDrivers = (
              dp: (typeof driversPanels)[number],
              role: 'correlates' | 'lever' | 'single',
              span: 6 | 12,
              fold?: typeof foldLead,
            ) => {
              const panelKey = dp.metricKey ?? data.key;
              const dimMetric = METRICS[panelKey];
              // Another metric's panel (seat time on Turns) speaks in its own
              // unit and has no secondary series.
              const ownMetric = panelKey === data.key;
              const sectionId = driversSectionId(dp, data.key, role);
              return (
                <DriversPanelSection
                  key={sectionId}
                  panel={dp}
                  metricKey={data.key}
                  sectionId={sectionId}
                  role={role}
                  fmtVal={
                    ownMetric
                      ? fmtVal
                      : v => formatDrillMetric(v, panelKey, moneyUnit)
                  }
                  fmtVal2={ownMetric ? fmtVal2 : undefined}
                  primaryLabel={dimMetric?.short ?? m?.short}
                  secondaryLabel={ownMetric ? m2?.short : undefined}
                  foldLeadTimeDim={fold}
                  compareLabel={data.comparable ? data.comparePeriod?.label : undefined}
                  span={span}
                />
              );
            };
            return (
              <>
                {singles.map(dp => renderDrivers(dp, 'single', driversSpan))}
                {/* Correlates left, outlets right — outlets hide when the
                    scope is a single restaurant. */}
                {correlates
                  ? renderDrivers(correlates, 'correlates', driversSpan, foldLead)
                  : leadPanel
                    ? renderDrivers(leadPanel, 'single', driversSpan)
                    : null}
                {showOutlets ? (
                  <DrillOutletSection
                    metricKey={data.key}
                    outlets={data.outlets}
                    secondaryOutlets={data.secondary?.outlets}
                    groupVal={groupVal}
                    fmtVal={fmtVal}
                    fmtVal2={fmtVal2}
                    onSelectOutlet={onSelectOutlet}
                    span={correlates || leadPanel ? 6 : 12}
                    compareLabel={data.comparable ? data.comparePeriod?.label : undefined}
                    outletBasis={data.outletBasis}
                  />
                ) : null}
                {levers.map(dp => renderDrivers(dp, 'lever', 12))}
              </>
            );
          })()}

          {regimePanel && (
            <DrillRegimeSection
              metricKey={data.key}
              panel={regimePanel}
              basis={data.basis}
              rhythm={data.occupancyRhythm}
            />
          )}

          <DrillRoomSection metricKey={data.key} split={data.roomSplit} />

          <DrillTurnsShiftSection
            metricKey={data.key}
            capacity={data.turnsCapacity}
            outletCount={agentCtx?.outletId ? 1 : Math.max(1, data.outlets?.length ?? 1)}
          />

          {brainPanel && (
            <DrillBrainSection metricKey={data.key} panel={brainPanel} />
          )}

          {posItemsPanel && (
            <DrillPosItemsSection metricKey={data.key} panel={posItemsPanel} />
          )}
          {data.posDeadHours && (
            <DrillDeadHoursSection metricKey={data.key} data={data.posDeadHours} />
          )}
          {data.sellNoShow && (
            <DrillSellNoShowSection metricKey={data.key} data={data.sellNoShow} />
          )}

          {rankingPanels.map((rp, i) => (
            <DrillRankingSection
              key={`rank${i}`}
              metricKey={data.key}
              panel={rp}
              sectionId={`ranking:${rp.title}`}
            />
          ))}

          {blockedImpactPanel && (
            <DrillBlockedSection
              metricKey={data.key}
              panel={blockedImpactPanel}
              bucketLabels={bucketLabels}
              compareLabel={data.comparable ? data.comparePeriod?.label : undefined}
            />
          )}
        </DrillDeck>
        <DrillDefinitionsFooter definition={data.definition} />
      </div>
    </div>
  );
}
